import React, {useCallback, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent} from 'helpers/hooks/utils';
import StyledSettingsHeader from 'components/molecules/Headers/SettingsHeader/SettingsHeader.styles';
import {H5} from 'components/atoms/Text/Typography/Typography';
import utils from 'helpers/utils';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from 'components/atoms/Layout/Box/Box';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import InfoPaper from 'components/molecules/Papers/InfoPaper/InfoPaper';
import constants from 'helpers/constants';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Search from '@mui/icons-material/Search';
import Clear from '@mui/icons-material/Clear';
import InlineForm from 'components/organisms/Forms/InlineForm/InlineForm';

const SettingsHeader = React.forwardRef((props, ref) => {
  const {
    title,
    info,
    buttons,
    search,
    searchField,
    onSearch,
    ...innerProps
  } = useComponentProps(props, 'SettingsHeader', {
    children: ['title', 'action'],
    styled: ['color']
  });

  const [showInfo, setShowInfo] = useState(false);

  const showSearch = Boolean(onSearch);

  const infoAction = useMemo(() => ({
    tooltip: 'Show info',
    onClick: () => setShowInfo((current) => !current),
    icon: InfoOutlined
  }), []);

  const searchFieldRef = useRef(null);
  const onSearchEvent = useEffectEvent(onSearch);
  const searchFields = useMemo(() => ([{
    name: 'search',
    label: 'Search',
    inlineLabel: 'search',
    type: constants.formFieldTypes.text,
    validation: constants.formFieldValidationTypes.text,
    initial: search,
    debounce: constants.debounce.search,
    FormFieldProps: {
      ref: searchFieldRef,
      autoFocus: false,
      hiddenLabel: true,
      size: 'smaller',
      radius: 'round'
    },
    prefix: <Icon icon={Search} />,
    postfix: search?.length > 0 ? <ActionIconButton action={{
      icon: <Icon icon={Clear}/>,
      onClick: (e) => {
        onSearchEvent?.('');
        searchFieldRef.current?.focus();
        e.preventDefault();
      }
    }} /> : null,
    ...searchField
  }]), [search, searchField, onSearchEvent]);

  const handleSearch = useCallback((field, value) => {
    onSearchEvent?.(value);
  }, [onSearchEvent]);

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledSettingsHeader ref={ref} {...innerProps}>
    <Box className="SettingsHeader-head">
      {title ? <Box className="SettingsHeader-title">{utils.isReactElement(title) ? title : <H5>{title}</H5>}</Box> : null}
      {(buttons || info || searchFields) ? <Box className="SettingsHeader-buttons">
        {info ? <ActionIconButton variant="outlined"
                                  action={infoAction}
                                  flatBackground={true}
                                  radius="round"
                                  density="denser" /> : null}
        {showSearch ? <InlineForm className="SettingsHeader-search"
                                  onChange={handleSearch}
                                  fields={searchFields} /> : null}
        {buttons}
      </Box> : null}
    </Box>
    {showInfo ? <ClickAwayListener onClickAway={() => setShowInfo(false)}>
      <Grow in={showInfo}>
        <InfoPaper className="SettingsHeader-info" info={info} elevation={1} />
      </Grow>
    </ClickAwayListener>: null}
  </StyledSettingsHeader>
});

SettingsHeader.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  title: PropTypes.any,
  info: PropTypes.any,
  buttons: PropTypes.any,
  search: PropTypes.string,
  searchField: PropTypes.object,
  onSearch: PropTypes.func
};

export default SettingsHeader;
